import { ArrowRight } from 'lucide-react';

import { HeroTerminalCard } from '../../../../features/home/components/HeroTerminalCard';
import { HERO_METRICS } from '../../../../features/home/landingMetrics';
import { ProjectCard } from '../../../../features/projects/components/ProjectCard';
import { ServiceCard } from '../../../../features/services/components/ServiceCard';
import { TestimonialCard } from '../../../../features/testimonials/components/TestimonialCard';
import { useI18n } from '../../../../shared/i18n';
import { localePath } from '../../../../shared/i18n/routing';
import { htmlToPlainText, useData } from '../../../../shared/lib';
import { BinaryRain, Button, Container, Eyebrow, Grid, Section } from '../../../../shared/ui';
import type { HomeData } from './+data';

/**
 * The landing page (Bitwize logo approval mock, 2026-08).
 *
 * Every block below is optional and hidden when its data is empty — a
 * brand-new install with no content renders the hero and the contact CTA
 * only, and is meant to look intentional rather than broken
 * (docs/features.md §1). The featured-projects heading stays
 * team-attributing ("Selected work", not "projects we delivered"): the
 * block can contain prior work, and LEG-04 applies to this heading exactly
 * as it does to the portfolio listing's.
 *
 * The one thing here that is neither CMS content nor site inventory is the
 * hero metric row — see `landingMetrics.ts` for what those figures are and
 * why they live in their own marked file.
 */
export default function Page() {
  const { locale, t, formatNumber } = useI18n();
  const {
    hero,
    copy,
    metrics,
    featuredServices,
    serviceIcons,
    featuredProjects,
    featuredTechnologies,
    technologyIcons,
    testimonials,
    testimonialPhotos,
    faqPreview,
  } = useData<HomeData>();

  /*
   * What the hero's metric row renders.
   *
   * The editor's tiles win outright when they have configured any; otherwise
   * the shipped claims in `landingMetrics.ts` stand. Deliberately not a
   * merge of the two — a row showing one edited tile beside two shipped ones
   * is a row nobody chose, and `landingMetrics.ts` is explicit that a figure
   * which stops being true should disappear rather than linger.
   */
  const heroMetrics =
    metrics.length > 0
      ? metrics
      : HERO_METRICS.map((metric) => ({ value: metric.value, label: t(metric.labelKey) }));

  return (
    // The dark palette and the falling-binary field are the shell's, not this
    // page's — `PublicLayout` stamps both on every public route. This page
    // adds only what is particular to it.
    <>
      {/*
        The hero is the one section that keeps the brand reference's dark
        navy band — everything else on the page runs the light paper theme.
        Scoped here rather than site-wide: `data-theme="dark"` flips every
        semantic token for this subtree only (PublicLayout doc comment), so
        `HeroTerminalCard` and the metric row need no dark-specific styling
        of their own.

        No section-level glow beyond the binary field. The reference lights
        this band from one place only — the terminal card's own bloom — and
        a second wash across the whole section would wash out the field the
        card is supposed to be floating over.
      */}
      <Section
        spacing="lg"
        theme="dark"
        className="hs-dark-band relative isolate overflow-hidden text-text"
      >
        <BinaryRain columns={24} rows={40} className="absolute inset-0 -z-10 opacity-40" />

        <Container className="grid grid-cols-1 items-center gap-16 lg:grid-cols-[1.05fr_.95fr]">
          <div className="flex flex-col items-start gap-4">
            <Eyebrow className="text-gold [&>span]:bg-gold">
              {copy.kicker || t('common.home.kicker')}
            </Eyebrow>
            <h1 className="max-w-[15ch] font-heading text-5xl font-semibold leading-[1.12] tracking-tight sm:text-6xl">
              {copy.headline || t('common.home.headline')}
            </h1>
            {/*
              The company's own words when it has set them, the mock's line
              when it has not — `general.tagline` is admin-editable and must
              win over shipped copy wherever it exists.
            */}
            <p className="max-w-[46ch] text-lg text-text-muted">
              {hero.tagline || t('common.home.lead')}
            </p>
            {hero.about ? <p className="max-w-[46ch] text-text-muted">{hero.about}</p> : null}

            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                asChild
                size="lg"
                variant="gold"
                iconEnd={<ArrowRight className="size-4 hs-icon-directional" aria-hidden="true" />}
              >
                <a href={localePath(locale, '/contact')}>
                  {copy.ctaPrimaryLabel || t('common.home.startProject')}
                </a>
              </Button>
              <Button asChild size="lg" variant="secondary">
                <a href={localePath(locale, '/projects')}>
                  {copy.ctaSecondaryLabel || t('common.home.viewWork')}
                </a>
              </Button>
            </div>

            {heroMetrics.length > 0 ? (
              <dl className="mt-8 flex flex-wrap gap-8">
                {heroMetrics.map((metric) => (
                  <div key={metric.label}>
                    <dd dir="ltr" className="font-heading text-3xl font-semibold text-text">
                      {metric.value}
                    </dd>
                    <dt className="hs-meta text-text-subtle">{metric.label}</dt>
                  </div>
                ))}
              </dl>
            ) : null}
          </div>

          <HeroTerminalCard />
        </Container>
      </Section>

      {featuredServices.length > 0 ? (
        <Section spacing="md" className="hs-reveal border-t border-border-translucent">
          <Container className="flex flex-col gap-10">
            <div className="flex flex-wrap items-end justify-between gap-6">
              <div className="flex flex-col gap-2.5">
                <Eyebrow>
                  <span dir="ltr">/services</span>
                </Eyebrow>
                <h2 className="font-heading text-2xl font-semibold text-text">
                  {copy.servicesHeading || t('common.home.featuredServices')}
                </h2>
              </div>
              {/*
                The count is the real number of published practices, not the
                mock's hard-coded "Six" — a subtitle that disagrees with the
                grid under it is the kind of detail a visitor notices.
              */}
              <p className="max-w-[36ch] text-text-muted">
                {t('common.home.servicesSubtitle', {
                  count: formatNumber(featuredServices.length),
                })}
              </p>
            </div>
            <Grid columns={3}>
              {featuredServices.map((service) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  icon={service.icon_media_id ? serviceIcons[service.icon_media_id] : undefined}
                />
              ))}
            </Grid>
          </Container>
        </Section>
      ) : null}

      {featuredProjects.length > 0 ? (
        <Section
          spacing="md"
          className="hs-reveal border-y border-border-translucent bg-surface-translucent"
        >
          <Container className="flex flex-col gap-10">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex flex-col gap-2.5">
                <Eyebrow>
                  <span dir="ltr">/projects</span>
                </Eyebrow>
                <h2 className="font-heading text-2xl font-semibold text-text">
                  {copy.projectsHeading || t('common.home.featuredProjects')}
                </h2>
              </div>
              <Button asChild size="sm" variant="secondary">
                <a href={localePath(locale, '/projects')}>{t('common.home.viewAllProjects')}</a>
              </Button>
            </div>
            <Grid columns={3}>
              {featuredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </Grid>
          </Container>
        </Section>
      ) : null}

      {featuredTechnologies.length > 0 ? (
        <Section spacing="md" className="hs-reveal border-b border-border-translucent">
          <Container className="flex flex-col gap-6">
            <div className="flex flex-col gap-2.5">
              <Eyebrow>
                <span dir="ltr">/technologies</span>
              </Eyebrow>
              <h2 className="font-heading text-2xl font-semibold text-text">
                {copy.technologiesHeading || t('common.home.technologiesStrip')}
              </h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {featuredTechnologies.map((tech) => {
                const icon = tech.icon_media_id ? technologyIcons[tech.icon_media_id] : undefined;
                return (
                  <a
                    key={tech.id}
                    href={localePath(locale, `/technologies/${tech.slug}`)}
                    className="flex items-center gap-2 rounded-full border border-border px-3 py-1.5 text-sm text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
                  >
                    {icon ? (
                      <img src={icon.url} alt="" className="size-4" aria-hidden="true" />
                    ) : null}
                    {tech.name}
                  </a>
                );
              })}
            </div>
          </Container>
        </Section>
      ) : null}

      {testimonials.length > 0 ? (
        <Section spacing="md" className="hs-reveal border-b border-border-translucent">
          <Container className="flex flex-col gap-10">
            <div className="flex flex-col gap-2.5">
              <Eyebrow>
                <span dir="ltr">/testimonials</span>
              </Eyebrow>
              <h2 className="max-w-[28ch] font-heading text-2xl font-semibold text-text">
                {copy.testimonialsHeading || t('common.home.testimonialsHeading')}
              </h2>
            </div>
            <Grid columns={3}>
              {testimonials.map((testimonial) => (
                <TestimonialCard
                  key={testimonial.id}
                  testimonial={testimonial}
                  photo={
                    testimonial.photo_media_id
                      ? testimonialPhotos[testimonial.photo_media_id]
                      : undefined
                  }
                />
              ))}
            </Grid>
          </Container>
        </Section>
      ) : null}

      {faqPreview.length > 0 ? (
        <Section spacing="md" className="hs-reveal border-b border-border-translucent">
          <Container className="flex max-w-prose flex-col gap-6">
            {/* Wraps rather than squeezing: at 360px the heading and the
                link fight for the row and the heading breaks mid-phrase. */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
              <h2 className="font-heading text-2xl font-semibold text-text">
                {copy.faqHeading || t('common.home.faqPreview')}
              </h2>
              {/* `py-1` for the tap target — a bare 14px line is 20px tall,
                  under the WCAG 2.5.8 minimum, and this one sits alone in a
                  row rather than inside a sentence. */}
              <a
                href={localePath(locale, '/faq')}
                className="py-1 text-sm font-medium text-accent hover:underline"
              >
                {t('common.home.viewAllFaq')}
              </a>
            </div>
            <ul className="flex flex-col gap-4">
              {faqPreview.map((entry) => (
                <li key={entry.id}>
                  <p className="font-medium text-text">{entry.question}</p>
                  {/*
                    `answer` is rich-text HTML. This is a two-line teaser, so
                    it is flattened to text rather than rendered as markup —
                    printing it raw showed the visitor the tags themselves.
                    The full answer is rendered as HTML on /faq by `FaqEntry`.
                  */}
                  <p className="mt-1 line-clamp-2 text-sm text-text-muted">
                    {htmlToPlainText(entry.answer)}
                  </p>
                </li>
              ))}
            </ul>
          </Container>
        </Section>
      ) : null}

      {/*
        The closing band: a wide tint across the full width rather than a
        boxed card, so the page ends on an open invitation instead of one
        more panel. The brand reference dark-bands its "get in touch"
        section the same way it dark-bands the hero, so this is the second
        and last scoped `data-theme="dark"` on the page — see the hero above.
      */}
      <Section spacing="lg" theme="dark" className="hs-reveal hs-dark-band text-center text-text">
        <Container width="prose" className="flex flex-col items-center gap-5">
          <h2 className="max-w-[22ch] font-heading text-3xl font-semibold tracking-tight text-text sm:text-4xl">
            {copy.closingHeading || t('common.home.contactCta')}
          </h2>
          <p className="max-w-[52ch] text-lg text-text-muted">
            {copy.closingBody || t('common.home.contactBody')}
          </p>
          <Button asChild size="lg" variant="gold" className="mt-2">
            <a href={localePath(locale, '/contact')}>
              {copy.closingButtonLabel || t('common.home.getInTouch')}
            </a>
          </Button>
        </Container>
      </Section>
    </>
  );
}
